import { Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LinkPreviewData, useFetchLinkPreview } from '../../../queries/link-previews';
import { AttachmentCategory, LinkPreviewAttachment } from '../lib/entities/attachment';

// URL regex that matches common URL patterns
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;

// Strip common trailing punctuation that gets captured from natural text
// e.g., "Check out https://example.com!" → "https://example.com"
const stripTrailingPunctuation = (url: string): string => url.replace(/[.,;:!?)]+$/, '');

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;

interface UseLinkPreviewOptions {
	editor: Editor | null;
	attachment: unknown | null;
	setAttachment: (attachment: LinkPreviewAttachment | null) => void;
	enabled?: boolean;
}

/**
 * Hook to detect URLs in editor text and fetch Open Graph previews.
 * Only fetches a preview if no attachment is already set.
 * Link previews have lower priority than other attachment types (video, GIF, image).
 */
export const useLinkPreview = ({ editor, attachment, setAttachment, enabled = true }: UseLinkPreviewOptions) => {
	const [isLoading, setIsLoading] = useState(false);
	const lastFetchedUrlRef = useRef<string | null>(null);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	// Track current attachment to avoid overwriting higher-priority attachments (video, GIF, etc.)
	const attachmentRef = useRef(attachment);
	attachmentRef.current = attachment;

	const { mutate: fetchLinkPreview } = useFetchLinkPreview({
		onSuccess: (data: LinkPreviewData) => {
			// Don't override existing attachments - they have higher priority than link previews
			if (attachmentRef.current) {
				setIsLoading(false);
				return;
			}

			// Only set attachment if we got an image - skip links that only return minimal metadata
			// (e.g., auth-gated pages like Slack that only return site_name)
			if (data.image_url) {
				const linkPreviewAttachment = new LinkPreviewAttachment({
					category: AttachmentCategory.LinkPreview,
					url: data.url,
					title: data.title,
					description: data.description,
					image_url: data.image_url,
					site_name: data.site_name,
				});
				setAttachment(linkPreviewAttachment);
			}
			setIsLoading(false);
		},
		onError: () => {
			setIsLoading(false);
		},
	});

	const detectAndFetchUrl = useCallback(() => {
		if (!editor || !enabled) return;

		// Don't fetch if an attachment is already set
		if (attachment) return;

		const text = editor.getText();
		const matches = text.match(URL_REGEX);

		if (!matches || matches.length === 0) {
			lastFetchedUrlRef.current = null;
			return;
		}

		// Use the first URL found, stripping any trailing punctuation
		const url = stripTrailingPunctuation(matches[0]);

		// Don't re-fetch the same URL
		if (url === lastFetchedUrlRef.current) return;

		lastFetchedUrlRef.current = url;
		setIsLoading(true);
		fetchLinkPreview(url);
	}, [editor, enabled, attachment, fetchLinkPreview]);

	// Listen for editor updates with debouncing
	useEffect(() => {
		if (!editor || !enabled) return;

		const handleUpdate = () => {
			// Clear existing timer
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			// Set new timer
			debounceTimerRef.current = setTimeout(() => {
				detectAndFetchUrl();
			}, DEBOUNCE_DELAY);
		};

		editor.on('update', handleUpdate);

		return () => {
			editor.off('update', handleUpdate);
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [editor, enabled, detectAndFetchUrl]);

	// Reset lastFetchedUrl when attachment is removed
	useEffect(() => {
		if (!attachment) {
			lastFetchedUrlRef.current = null;
		}
	}, [attachment]);

	return {
		isLoading,
	};
};
